import React, { FunctionComponent } from 'react';
import styled from 'styled-components';

import THEME from 'theme';
import { Modal } from 'components';
import { Heading, ListItem, Text, List } from 'components/chroma';

import { Excess, ExcessCode } from 'scenes/common/types';
import { replaceSpaces } from 'scenes/common/helpers/text';

const EXCESS_TYPE = 'Excess type';

const excessTableRowTitle: Record<string, string> = {
  [ExcessCode.BaseAmt]: 'Standard excess',
  [ExcessCode.VoluntaryAmt]: 'Nominated excess',
  [ExcessCode.PortableContentsCover]:
    'Portable contents cover excess (if applicable)',
};

const excessDescriptionHeading: Record<string, string> = {
  ...excessTableRowTitle,
  [ExcessCode.PortableContentsCover]: 'Portable contents cover excess',
};

const Container = styled.div`
  margin-bottom: 1.5rem;
  tr {
    border-bottom: 1px solid ${THEME.FIELD.BORDER.LIGHT};
  }
  th,
  td {
    padding: 0.5rem 3rem 0.5rem 0;
  }
`;

const ListContainer = styled.div`
  font-size: 1rem;
  margin-bottom: 1.5rem;
`;

const TableHeader: FunctionComponent<{
  label: string;
}> = ({ label }) => (
  <thead>
    <tr>
      <th scope="col" id={replaceSpaces(label)}>
        <Text weight={700} margin={{ bottom: 0 }}>
          {label}
        </Text>
      </th>
      <th scope="col" id={replaceSpaces(`${label}_amount`)}>
        <Text weight={700} margin={{ bottom: 0 }}>
          Amount
        </Text>
      </th>
    </tr>
  </thead>
);

const TableRow: FunctionComponent<{
  label: string;
  amount: number;
  name: string;
}> = ({ label, amount, name }) => (
  <tr>
    <td headers={replaceSpaces(label)}>
      <Text margin={{ bottom: 0 }}>{name}</Text>
    </td>
    <td headers={`${label}-amount`}>
      <Text margin={{ bottom: 0 }}>${amount}</Text>
    </td>
  </tr>
);

const ExcessHeader: FunctionComponent = ({ children }) => (
  <Heading size={2} margin={{ bottom: 1 }}>
    {children}
  </Heading>
);

const ExcessModal: FunctionComponent<{
  isOpen: boolean;
  excesses: Excess[];
  onClose: () => void;
  hasContentsCoverType: boolean;
}> = ({ isOpen, excesses, onClose, hasContentsCoverType }) => (
  <Modal header="What is an excess?" isOpen={isOpen} onClose={onClose}>
    <Text>
      An excess is the amount you contribute towards the cost of your claim.
    </Text>

    <Text margin={{ bottom: 0 }}>There are 3 types of excesses:</Text>
    <ListContainer>
      <List bullet>
        <ListItem key="standard-excess">Standard excess</ListItem>
        <ListItem key="nominated-excess">Nominated excess</ListItem>
        <ListItem key="portable-contents-cover-excess">
          Portable contents cover excess
        </ListItem>
      </List>
    </ListContainer>

    <Heading size={2} margin={{ bottom: 0 }}>
      Excess details
    </Heading>
    <Container>
      <table>
        <TableHeader label={EXCESS_TYPE} />
        <tbody>
          {excesses.map((excess) => (
            <TableRow
              key={excess.name}
              label={EXCESS_TYPE}
              name={excess.name ?? ''}
              amount={excess.amount}
            />
          ))}
          {hasContentsCoverType && (
            <TableRow
              key={ExcessCode.PortableContentsCover}
              label={EXCESS_TYPE}
              name={excessTableRowTitle[ExcessCode.PortableContentsCover]}
              amount={100}
            />
          )}
        </tbody>
      </table>
    </Container>

    <ExcessHeader>{excessDescriptionHeading[ExcessCode.BaseAmt]}</ExcessHeader>
    <Text>The Standard excess may apply when we accept a claim from you.</Text>

    <ExcessHeader>
      {excessDescriptionHeading[ExcessCode.VoluntaryAmt]}
    </ExcessHeader>
    <Text>
      You may choose to vary your Standard excess. Your Nominated excess
      includes your standard excess amount. The Nominated excess applies for
      optional extras including Motor burn out and Accidental damage, but does
      not apply to Portable contents cover.
    </Text>
    <Text>
      You can reduce your premium by choosing a higher nominated excess. This
      means you choose to pay a lesser premium, but you pay a higher excess if
      you need to make a claim.
    </Text>
    <Text>
      Or you can increase your premium by choosing a lower nominated excess.
      This means you choose to pay a higher premium, but you pay a lower excess
      if you need to make a claim.
    </Text>

    <ExcessHeader>
      {excessDescriptionHeading[ExcessCode.PortableContentsCover]}
    </ExcessHeader>
    <Text>
      This excess is applicable for Portable contents cover. If you make a claim
      for loss or damage to your Portable contents only and you pay the Portable
      cover excess, you don’t have to pay the Standard excess or any Nominated
      excess you may have chosen.
    </Text>
  </Modal>
);

export default ExcessModal;
